import classNames from "classnames";
import moment from "moment";
import { useRouter } from "next/router";
import React, { useContext } from "react";
import { IntlContext, defineMessages } from "react-intl";
import { useSelector } from "react-redux";
import { components } from "react-select";

import {
  OptionsByDate,
  getOptionDates,
} from "shared/components/Flights/FlightStream";
import { FlightSegment } from "shared/gol-api/types/flightSearch";
import { dayMonthFormat } from "shared/lib/datetime";
import {
  capitalizeOnlyFirstLetter,
  capitalizedMomentWeekday,
} from "shared/lib/functions";
import { formattedMessageParams } from "shared/messages";

import Img from "@components/FileServer/Img";
import DiffDays from "@components/Flights/DiffDays";
import FormattedMessage from "@components/UI/FormattedMessage";
import SimpleSelect from "@components/UI/SimpleSelect";

import { AppState } from "@shared/data/reducers";

const { flightDetail } = defineMessages({
  flightDetail: formattedMessageParams("FlightStream.flightDetail"),
});

export function DropdownIndicator(props) {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <Img src="/static/images/ico-arrow-down.svg" />
      </components.DropdownIndicator>
    )
  );
}

interface Props {
  toggleShowDetailStream: () => void;
  showDetails: boolean;
  firstSegment: FlightSegment;
  index: number;
  selectedDate?: string;
  dateDeparture?: string;
  optionsByDate?: OptionsByDate;
  changeSelectedStreamParts?: (streamIndex: number, value: string) => void;
  isMulticity: boolean;
}

export default function FlightStreamHeader({
  changeSelectedStreamParts,
  dateDeparture,
  firstSegment: { DepartureDateTime },
  index,
  optionsByDate,
  selectedDate,
  toggleShowDetailStream,
  showDetails,
  isMulticity,
}: Props) {
  const intl = useContext(IntlContext);
  const router = useRouter();
  const { searchQuery, lang, defaultCountry } = useSelector(
    (state: AppState) => ({
      searchQuery: state.storage.searchQuery,
      lang: state.requestorConfig.currentLanguage,
      defaultCountry:
        state.storage.frontendSettings?.dealerFrontendSettings
          ?.defaultCountry ?? "",
    })
  );

  const isReservationPage = router.route.includes("reservation");
  const isSpecialOffer = router.query.isSpecialOffer === "true";
  const isMultipleFlight = router && router.query.flight_1_origin !== undefined;
  const isDeparture =
    Number(index) === 0 || isMulticity ? true : isMultipleFlight;

  const optionDates = getOptionDates(optionsByDate);

  return (
    <div className="flight-segment-header">
      <div
        className={`flight-segment-title flight-segment-title__${
          isDeparture ? "departure" : "arrivals"
        }`}
      >
        <>
          <span className="flight-segment-title-typeroute">
            {isDeparture ? (
              <FormattedMessage id="Flight.departure" />
            ) : (
              <FormattedMessage id="FlightAdditionalInfo.return" />
            )}
          </span>

          {optionDates.length > 1 ? (
            <SimpleSelect
              className={`flight-segment-date-select flight-segment-date-select-${index}`}
              styles={{
                control: () => ({
                  display: "flex",
                  maxHeight: "28px",
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: "14px",
                  minWidth: 220,
                }),
              }}
              clearIndicator={null}
              clearable={false}
              isSearchable={false}
              classNamePrefix={`flight-segment-date-select-${index}`}
              components={{
                DropdownIndicator,
                IndicatorSeparator: () => null,
                ValueContainer(props) {
                  return (
                    <div className="flight-segment-date-select-val">
                      {props.children}
                    </div>
                  );
                },
                SingleValue(props) {
                  const [date, day] = props.data.label
                    .split(/\s+/)
                    .map((l) => l.trim());
                  return (
                    <>
                      <span className="flight-segment-title-datum">{date}</span>{" "}
                      <span className="flight-segment-title-dayofweek">
                        {day}
                      </span>
                    </>
                  );
                },
              }}
              value={{
                value: selectedDate,
                label: `${moment(dateDeparture).format(
                  dayMonthFormat(defaultCountry)
                )} ${capitalizedMomentWeekday(dateDeparture, lang)}`,
              }}
              options={optionDates.map((d) => ({
                value: d,
                label: `${d} ${capitalizedMomentWeekday(
                  optionsByDate[d][0].DepartureDateTime,
                  lang
                )}`,
              }))}
              onChange={(e) => {
                const selectedOption = optionsByDate[e.value][0];
                changeSelectedStreamParts(index + 1, selectedOption.Key);
              }}
            />
          ) : (
            <>
              <span className="flight-segment-title-datum">
                {moment(dateDeparture).format(dayMonthFormat(defaultCountry))}
              </span>
              <span className="flight-segment-title-dayofweek">
                {capitalizeOnlyFirstLetter(
                  moment(new Date(DepartureDateTime))
                    .locale(lang)
                    .format("dddd")
                    .toUpperCase()
                )}
              </span>
            </>
          )}
          {!isReservationPage && !isSpecialOffer && (
            <DiffDays
              dateDeparture={dateDeparture}
              index={index}
              searchQuery={searchQuery}
            />
          )}
        </>
        <span
          onClick={toggleShowDetailStream}
          className="flight-segment-title-link"
          role="button"
        >
          {intl.formatMessage(flightDetail)}
          <Img
            src="/static/images/ico-arrow-down.svg"
            className={classNames("flight-segment-title-link-toggle-icon", {
              "select-arrow-rotate-up": showDetails,
            })}
            alt="down arrow icon"
          />
        </span>
      </div>
    </div>
  );
}
